import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { BarraFila } from '@/utils/equiplexQualidade';
import { fmtCompacto, fmtData, fmtUn } from '@/utils/painelFormat';
import { ALTURA_BARRA_HORIZONTAL, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import TooltipBox from '../TooltipBox';

interface Props {
  barras: BarraFila[];
  corte: string;
  cores: CoresPainel;
  onFaixa: (faixa: string) => void;
}

/** A fila do CQ por tempo de espera. Clicar numa faixa filtra a tabela de lotes. */
const FilaEsperaChart: FC<Props> = ({ barras, corte, cores, onFaixa }) => {
  const total = barras.reduce((acc, b) => acc + b.lotes, 0);

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const b = active && payload?.[0] ? (payload[0].payload as BarraFila) : null;
    if (!b) return null;
    return (
      <TooltipBox
        titulo={`Esperando ${b.rotulo}`}
        linhas={[
          { rotulo: 'Lotes', valor: fmtUn(b.lotes), cor: cores.programado },
          { rotulo: 'Unidades', valor: fmtUn(b.quantidade) },
        ]}
        rodape="Clique para ver os lotes na tabela"
      />
    );
  };

  return (
    <PainelCard
      testId="qualidade-fila"
      titulo="Lotes aguardando liberação, por tempo de espera"
      subtitulo={`${fmtUn(total)} lotes pendentes, contando até ${fmtData(corte)}.`}
    >
      {total === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">Nenhum lote aguardando o CQ.</div>
      ) : (
        <div style={{ width: '100%', height: Math.max(220, barras.length * ALTURA_BARRA_HORIZONTAL + 30) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barras} layout="vertical" margin={{ top: 0, right: 48, bottom: 0, left: 8 }} barCategoryGap={8}>
              <CartesianGrid horizontal={false} stroke={cores.grade} />
              <XAxis type="number" allowDecimals={false} tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="rotulo" width={144} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar
                dataKey="lotes"
                fill={cores.programado}
                radius={[0, 4, 4, 0]}
                maxBarSize={24}
                isAnimationActive={false}
                cursor="pointer"
                onClick={(d: unknown) => onFaixa((d as { payload?: BarraFila }).payload?.faixa ?? '')}
              >
                <LabelList dataKey="lotes" position="right" style={{ fill: cores.texto, fontSize: FONTE_ROTULO, fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PainelCard>
  );
};

export default FilaEsperaChart;
